/**
 * Coastline theme layer — global entry module.
 *
 * Loaded once from layout/theme.liquid as a native ES module, after Venue's
 * import map (a module script above the map would make browsers without
 * multiple-import-map support reject it). It has no static imports; it
 * import()s the Venue runtime patches listed in the #ch-module-urls map (see
 * the end of this file). Module scripts are deferred, so this runs after the
 * document is parsed. Behaviours live in their own custom-element modules
 * (assets/ch-<element>.js), loaded by the sections that need them; nothing is
 * exposed on window.
 *
 * Seven things live here: the `ch-js` class, the motion leash for Venue's
 * <lazy-video> and for an autoplaying Venue carousel, Escape for the header
 * menus, --ch-header-overlap, the guard that keeps Venue's body.header-stuck
 * true to the header's position, and the loader for the Venue runtime
 * patches (assets/ch-venue-a11y.js, assets/ch-image-sizes.js).
 *
 * `ch-js` on <html> marks that the Coastline layer's JavaScript is running.
 * It arrives after first paint: use it to enhance (reveal JS-only controls),
 * never to hide content by default.
 */
document.documentElement.classList.add('ch-js');

/*
 * Motion leash for Venue's own <lazy-video> (snippets/lazy-video.liquid, used
 * by Venue's image-with-text, banner, grid, gallery … video blocks). Venue
 * autoplays those clips with no reduced-motion or Save-Data check. Coastline's
 * rule (docs/01-brand.md, Motion) is still-only for those visitors, so any
 * lazy-video clip that starts playing for them is paused on its first frame.
 * <ch-moving-still> applies the same test itself (assets/ch-moving-still.js).
 * `play` doesn't bubble, so this listens in the capture phase.
 */
const SLOW_CONNECTIONS = ['slow-2g', '2g'];
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function prefersStill() {
  const connection = navigator.connection;
  return reducedMotion.matches || Boolean(connection?.saveData) || SLOW_CONNECTIONS.includes(connection?.effectiveType);
}

function isVenueLazyVideo(target) {
  return target instanceof HTMLVideoElement && target.closest('lazy-video') !== null;
}

document.addEventListener(
  'play',
  (event) => {
    if (isVenueLazyVideo(event.target) && prefersStill()) event.target.pause();
  },
  true,
);

/*
 * The same leash for Venue's own autoplaying carousels (<media-carousel
 * data-autoplay>: Venue's slideshow and image-carousel sections). Coastline's
 * slideshow is ch-slideshow, which never rotates under reduced motion; this
 * covers a merchant adding Venue's section anyway. Venue builds its Swiper
 * lazily, when the carousel nears the viewport, and announces it with
 * on:media-carousel:init (not bubbling: capture phase); stop() is Venue's
 * own method (assets/media-carousel.js). The theme editor restarts a
 * carousel when its block is deselected (assets/editor.js), so that is
 * caught too. Under reduced motion only; nothing restarts a carousel when
 * the preference is turned off again, the visitor's arrows still work.
 */
const AUTOPLAY_CAROUSEL = 'media-carousel[data-autoplay]';

function stopCarousel(carousel) {
  if (typeof carousel?.stop === 'function') carousel.stop();
}

function stopAutoplayCarousels() {
  if (reducedMotion.matches) document.querySelectorAll(AUTOPLAY_CAROUSEL).forEach(stopCarousel);
}

document.addEventListener(
  'on:media-carousel:init',
  (event) => {
    if (reducedMotion.matches && event.target instanceof Element && event.target.matches(AUTOPLAY_CAROUSEL)) stopCarousel(event.target);
  },
  true,
);

document.addEventListener('shopify:block:deselect', () => requestAnimationFrame(stopAutoplayCarousels));
document.addEventListener('shopify:section:load', () => requestAnimationFrame(stopAutoplayCarousels));
stopAutoplayCarousels();

reducedMotion.addEventListener('change', () => {
  if (!reducedMotion.matches) return;
  document.querySelectorAll('lazy-video video').forEach((video) => video.pause());
  stopAutoplayCarousels();
});

/*
 * Escape closes an open header menu (the mega panel, any dropdown).
 * Venue's <header-details-disclosure> opens on hover and on Enter but only
 * closes when the pointer or the focus leaves, so a panel over the page
 * could not be dismissed without moving away (WCAG 1.4.13). close() is
 * Venue's own method on that element (assets/header-details-disclosure.js).
 * When the focus was inside the panel it returns to the item's summary, the
 * control that opened it; a panel opened by hover leaves the focus alone.
 */
const HEADER_MENU = '.header header-details-disclosure > details';
const OPEN_HEADER_MENU = `${HEADER_MENU}[open]`;

/*
 * Venue's close() writes aria-expanded="false" on the summary, but a menu
 * opened from the keyboard is toggled by the browser and nothing writes
 * "true" back: after one close (Escape here, or Venue's own focus-out) a
 * reopened menu was announced as collapsed. The attribute follows the
 * element's real state. `toggle` doesn't bubble: capture phase.
 */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(HEADER_MENU)) return;
    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
  },
  true,
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  document.querySelectorAll(OPEN_HEADER_MENU).forEach((details) => {
    const hadFocus = details.contains(document.activeElement);
    const disclosure = details.parentElement;

    if (typeof disclosure.close === 'function') disclosure.close();
    else details.removeAttribute('open');

    if (hadFocus) details.querySelector('summary')?.focus();
  });
});

/*
 * Transparent-header overlap → --ch-header-overlap on <html>.
 * Venue re-measures --header-height only when the in-flow <header-container>
 * resizes (snippets/header-scripts.liquid). A transparent header is absolutely
 * positioned, so that wrapper stays 0px tall and the value goes stale as soon
 * as the header itself changes height — the logo loading, a web-font swap, the
 * theme editor resizing its preview (e.g. 55px measured at phone width, 89px
 * actual at 1000px). Sections that sit under the header (ch-drop-hero) read
 * this instead: the header's height while it overlays the page, 0 while it's in
 * flow. Sticky/scroll states only change the header's colors and transform, not
 * its height, so this never shifts the layout on scroll.
 */
const headerResize = new ResizeObserver(() => setHeaderOverlap());
let observedHeader = null;

function setHeaderOverlap() {
  const header = document.querySelector('.js-header');

  // The theme editor re-renders the header section as a new element.
  if (header !== observedHeader) {
    if (observedHeader) headerResize.unobserve(observedHeader);
    if (header) headerResize.observe(header);
    observedHeader = header;
  }

  const position = header ? getComputedStyle(header).position : 'static';
  const overlap = position === 'absolute' || position === 'fixed' ? header.offsetHeight : 0;
  document.documentElement.style.setProperty('--ch-header-overlap', `${overlap}px`);
}

setHeaderOverlap();

// After Venue's editor.js has handled the reload (it can toggle the header's
// transparent state without changing its size).
document.addEventListener('shopify:section:load', () => requestAnimationFrame(setHeaderOverlap));

/*
 * body.header-stuck, kept true to the header's real position.
 * Venue's <header-container> (assets/header-container.js) sets that class
 * from an IntersectionObserver whose callback reads only the FIRST entry of
 * each batch: ([e]) => body.classList.toggle('header-stuck', !e.isIntersecting).
 * When the state changes twice before the callback runs, the batch holds two
 * entries for the container in time order, the first one stale (seen on the
 * dev preview: { isIntersecting: false, rootBounds 1×1 } then { true,
 * 1280×900 }, after a capture beyond the viewport, DevTools' "Capture full
 * size screenshot"). Venue applies the stale one and the observer has nothing
 * new to report, so the transparent header over the home hero stays solid at
 * scroll 0 until the visitor scrolls past it and back (docs/03-venue-audit.md,
 * Known Venue issues).
 * This watches the same element with the same threshold, takes the LAST
 * entry and re-applies Venue's own rule one frame later, after every observer
 * callback of the batch has run, so it never depends on which observer the
 * browser calls first. Same semantics as Venue's: stuck = the sticky
 * container is not wholly inside the viewport. Headers that aren't sticky
 * have no data-sticky and are left alone, as Venue leaves them.
 */
const stuckWatch = new IntersectionObserver(
  (entries) => {
    const latest = entries[entries.length - 1];
    requestAnimationFrame(() => {
      if (latest.target.isConnected) document.body.classList.toggle('header-stuck', !latest.isIntersecting);
    });
  },
  { threshold: [1] },
);
let watchedContainer = null;

function watchHeaderStuck() {
  const container = document.querySelector('header-container[data-sticky]');
  if (container === watchedContainer) return;
  if (watchedContainer) stuckWatch.unobserve(watchedContainer);
  if (container) stuckWatch.observe(container);
  watchedContainer = container;
}

watchHeaderStuck();

// The theme editor re-renders the header section as a new element.
document.addEventListener('shopify:section:load', () => requestAnimationFrame(watchHeaderStuck));

/*
 * Venue runtime patches (docs/03-venue-audit.md, Runtime patches). Venue core
 * is read-only, so assets/ch-venue-a11y.js (quick view and quick add made
 * focusable and named, the stock note a polite live region, sold-out and
 * unavailable notes on option buttons) and assets/ch-image-sizes.js (image
 * `sizes` that match the flush, 2560px-wide layout) patch Venue's markup at
 * runtime. snippets/ch-fonts.liquid prints their versioned URLs as a JSON
 * map in <head> (a module tag there would sit above Venue's import map);
 * each one is a side-effect module, imported here once. A failed import, or
 * a broken map, loads nothing and stays quiet: the page works without them
 * (docs/07-system-design.md §10).
 */
const moduleUrls = document.getElementById('ch-module-urls');

if (moduleUrls) {
  try {
    Object.values(JSON.parse(moduleUrls.textContent)).forEach((url) => {
      if (typeof url === 'string') import(url).catch(() => {});
    });
  } catch {
    /* A broken map loads no patches. */
  }
}
